import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingsMetadata, BymoPagePropsContentSettingSpecialSemanticMeaning,
    DoppeDtoBlogPost
} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    doppeDtoBlogPostGetMedia,
    DoppeExternalBlogPostProvider,
    doppeExternalBlogPostProviderResolveBusinessEntityReference,
    DoppeLinkSpecEntityType,
    doppeLinkSpecCreateEntity
} from '../../client-server-common';
import {DoppeSupportedWixTpas} from '../../client-server-common/types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../../client-server-common/types/doppe-dto-user';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {RichTextContent, SerializedDateTimeValueAsString, HideableValue, OverridableValue } from '@wix/devzai-utils-common';
import {
    arrayRemoveNullishValues,
    arrayRemoveNullValues,
    dateConvertToIsoString,
    dateGetCurrentDateTime,
    dateSerializedToString,
    evaluateFunction,
    hideableValueGetValue,
    stringIsNotNullableOrWhiteSpace,
    hideableValueModifyValue,
    immerProduce,
    hideableValueCreateHidden,
    overridableValueCreateOriginal,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    overridableValueIsOriginal } from '@wix/devzai-utils-common';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    doppeActionCtaButtonTypeGetDefaultSettings,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonCreate
} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeBlogPostActionGetMediaViewDefaultSettings
} from '../../doppe-action-types-client-server-common/doppe-blog-post-action-types-utils/doppe-blog-post-action-types-utils';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';

export namespace DoppeActionTypeBlogPost {

    export type ArticleSettings = DoppeActionMediaViewSettings &
        {
            blogPostLink: string;
            blogPostTitle: HideableValue<OverridableValue<string>>;
            description: HideableValue<OverridableValue<RichTextContent>>;
            blogPostImages: OverridableValue<WixMediaResource[] | null>;
            blogPostDate: HideableValue<OverridableValue<SerializedDateTimeValueAsString>>;
            showBlogWebsite: boolean;
            blogPostProvider: DoppeExternalBlogPostProvider | null;
            blogPostProviderId: DoppeDtoBlogPost.Id | null;
        }

    export interface ActionSettings extends DoppeActionTypeBlogPost.ArticleSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {

    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        blogPostButtonText?: HideableValue<string>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeBlogPostActionContentSettingsMetadata = {
    blogPostTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    blogPostDate: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.DateTimeString, BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    blogPostLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    blogPostImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    showBlogWebsite: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showBlogWebsite'}),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeBlogPost.ActionSettings>


export const doppeActionTypeBlogPost = doppeActionTypeDefineMetadata<DoppeActionTypeBlogPost.ActionSettings, DoppeActionTypeBlogPost.PartialProps>({
    id: 'io.bymo.action/blogPost',
    resolveActionSettings: action => {

        const {
            blogPostLink = '',
            blogPostButtonText = '',
            ctaButton,
            ctaButtons,
            sections,
            mainMediaViewSettings,
            mainMediaViewLayout,
            ...restSettings
        } = action.settings;

        return {
            blogPostLink: blogPostLink,
            blogPostTitle: '',
            description: '',
            blogPostImages: null,
            blogPostDate: dateConvertToIsoString(dateGetCurrentDateTime()),
            showBlogWebsite: true,
            blogPostProvider: null,
            blogPostProviderId: null,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: blogPostButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeBlogPostActionGetMediaViewDefaultSettings({
                mainMediaViewSettings: mainMediaViewSettings,
                mainMediaViewLayout: mainMediaViewLayout,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, true),
            ...restSettings,
        }
    },
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeBlogPostActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                blogPostDate: true,
                blogPostTitle: true,
                description: true
            },
        bindableContentSettingsNames: {
                blogPostDate: true,
                blogPostImages: true,
                blogPostTitle: true,
                description: true
            }
    },
    actionListItemSettingsMetadata: false,
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.blogPostLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveUsedMedia: actionSettings => {
        return overridableValueGetValue(actionSettings.blogPostImages) ?? [];
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const blogPost = actionSettings.blogPostProvider !== null && actionSettings.blogPostProviderId !== null ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalBlogPostProviderResolveBusinessEntityReference(actionSettings.blogPostProvider!, actionSettings.blogPostProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBlogPost : null;
        })() : null;

        const blogPostImages = overridableValueGetOverriddenValue(
            actionSettings.blogPostImages,
            blogPost ? doppeDtoBlogPostGetMedia(blogPost) : null
        );
        return blogPostImages?.[0] ?? null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const sourceLinkSpec = evaluateFunction(() => {
            if (actionSettings.blogPostProvider === DoppeExternalBlogPostProvider.Hopp && actionSettings.blogPostProviderId !== null) {
                return doppeLinkSpecCreateEntity(DoppeLinkSpecEntityType.BlogPost, actionSettings.blogPostProviderId);
            }
            return stringIsNotNullableOrWhiteSpace(actionSettings.blogPostLink) ? actionSettings.blogPostLink : null;
        });

        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: sourceLinkSpec,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            const images = overridableValueGetValue(actionSettings.blogPostImages);
            if (images) {
                actionSettings.blogPostImages = images.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap));
            }
        })
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.blogPostProvider !== null && actionSettings.blogPostProviderId !== null ?
                doppeExternalBlogPostProviderResolveBusinessEntityReference(
                    actionSettings.blogPostProvider,
                    actionSettings.blogPostProviderId
                ) : null
        ])
    },
    resolveActionEntityBinding: actionSettings => {
        const {blogPostProvider, blogPostProviderId} = actionSettings;
        return blogPostProvider !== null && blogPostProviderId !== null
            ? doppeExternalBlogPostProviderResolveBusinessEntityReference(blogPostProvider, blogPostProviderId)
            : null;
    },
    getSupportedBoundEntities: ({linkedMetaSiteInstalledTpas, creatorsMetaSiteInstalledTpas}) =>
        arrayRemoveNullishValues([
            linkedMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.Blog) ? DoppeBusinessEntity.WixBlogPost : undefined,
            creatorsMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.Blog) ? DoppeBusinessEntity.HoppBlogPost : undefined,
        ]),
    getSupportedBoundEntitiesByTpa: () => ({
        [DoppeDtoUserMetaSiteType.LinkedMetaSite]: {
            [DoppeSupportedWixTpas.Blog]: [DoppeBusinessEntity.WixBlogPost],
        },
        [DoppeDtoUserMetaSiteType.CreatorsMetaSite]: {
            [DoppeSupportedWixTpas.Blog]: [DoppeBusinessEntity.HoppBlogPost],
        },
    }),
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {blogPostProvider, blogPostProviderId} = actionSettings;
        if (blogPostProvider !== null && blogPostProviderId !== null) {
            const boundBlogPost = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalBlogPostProviderResolveBusinessEntityReference(blogPostProvider, blogPostProviderId)
            );
            if (boundBlogPost && !doppeBusinessEntityDtoIsNotFound(boundBlogPost)) {
                return {
                    ...actionSettings,
                    ...doppeActionTypeBlogPostResolveSettingsFromBoundEntity(actionSettings, boundBlogPost as DoppeDtoBlogPost),
                };
            }
        }
        return actionSettings;
    },
});


export function populateBlogPostActionSettingsDefaultTexts(options: {
    blogPost: DoppeActionTypeBlogPost.ArticleSettings,
    pageContentLocales: DoppeAppPageContentLocales,
}): DoppeActionTypeBlogPost.ActionSettings {

    const {
        blogPost,
        pageContentLocales,
    } = options;

    const defaultButtonText = pageContentLocales['blogPost.actionSettings.buttonText']

    return {
        ...blogPost,
        sections: [],
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [doppeActionMultipleCTAButtonCreate({
                text: defaultButtonText,
                isRequired: false,
                settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
            })],
        }, false),
        blogPostTitle: blogPost.blogPostTitle ?? pageContentLocales['blogPost.actionSettings.title'],
        description: blogPost.description ?? hideableValueCreateHidden(pageContentLocales['blogPost.actionSettings.description'])
    }
}

export function doppeActionTypeBlogPostHasAnyOriginalOverridableField(settings: DoppeActionTypeBlogPost.ArticleSettings): boolean {
    return overridableValueIsOriginal(settings.blogPostImages) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.blogPostTitle)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.description)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.blogPostDate));
}

export function doppeActionTypeBlogPostMigrateOverridableValues<T extends DoppeActionTypeBlogPost.ArticleSettings>(settings: T): T {
    if (settings.blogPostProvider !== null && !doppeActionTypeBlogPostHasAnyOriginalOverridableField(settings)) {
        return {
            ...settings,
            blogPostDate: hideableValueModifyValue(
                settings.blogPostDate,
                value => overridableValueCreateOriginal(overridableValueGetValue(value))
            ),
        };
    }

    return settings;
}

export function doppeActionTypeBlogPostResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeBlogPost.ArticleSettings,
    doppeDtoBlogPost: DoppeDtoBlogPost
): DoppeActionTypeBlogPost.ArticleSettings {
    return {
        ...actionSettings,
        blogPostTitle: hideableValueModifyValue(
            actionSettings.blogPostTitle,
            value => overridableValueGetOverriddenValue(value, doppeDtoBlogPost.title ?? '')
        ),
        description: hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, doppeDtoBlogPost.excerpt ?? '')
        ),
        blogPostImages: overridableValueGetOverriddenValue(
            actionSettings.blogPostImages,
            doppeDtoBlogPostGetMedia(doppeDtoBlogPost)
        ),
        blogPostDate: (() => {
            const publishedDate = doppeDtoBlogPost.firstPublishedDate;
            return publishedDate ?
                hideableValueModifyValue(
                    actionSettings.blogPostDate,
                    value => overridableValueGetOverriddenValue(value, dateSerializedToString(publishedDate))
                ) :
                hideableValueCreateHidden(dateSerializedToString(new Date(0)));
        })(),
        showBlogWebsite: stringIsNotNullableOrWhiteSpace(doppeDtoBlogPost.blogPostPageUrl) ?
            actionSettings.showBlogWebsite : false,
        blogPostLink: stringIsNotNullableOrWhiteSpace(doppeDtoBlogPost.blogPostPageUrl) ?
            doppeDtoBlogPost.blogPostPageUrl : 'about:blank',
    }
}
